import { cn } from '@/lib/utils';

export interface FieldProps {
  /** Real <label> text. Every input in this app gets one — placeholders are not labels. */
  label?: React.ReactNode;
  htmlFor?: string;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * Shared label / hint / error scaffolding. Inputs wire their `aria-describedby`
 * and `aria-invalid` to the ids produced here, so a screen reader announces the
 * validation message with the field rather than as loose text.
 */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  required,
  className,
  children,
}: FieldProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label ? (
        <label
          htmlFor={htmlFor}
          className="flex items-center gap-1 text-xs font-medium text-muted"
        >
          {label}
          {required ? (
            <span className="text-danger" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
      ) : null}
      {children}
      {error ? (
        <p id={htmlFor ? `${htmlFor}-error` : undefined} className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={htmlFor ? `${htmlFor}-hint` : undefined} className="text-xs text-fg-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** The one control surface every text-like input shares. */
export const controlClasses =
  'w-full rounded-md border border-subtle bg-surface px-2.5 text-fg shadow-xs ' +
  'placeholder:text-fg-subtle transition-colors ' +
  'hover:border-strong ' +
  'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25 ' +
  'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-fg-subtle ' +
  'aria-[invalid=true]:border-danger aria-[invalid=true]:focus:ring-danger/25';

export function describedBy(id: string, hasError: boolean, hasHint: boolean): string | undefined {
  const ids: string[] = [];
  if (hasError) ids.push(`${id}-error`);
  else if (hasHint) ids.push(`${id}-hint`);
  return ids.length ? ids.join(' ') : undefined;
}
